import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SecurityUpdateGoodIcon from '@mui/icons-material/SecurityUpdateGood';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import { listRoles } from '../../../services/SecurityRolesService';
import { listGroups } from '../../../services/SecurityGroupsService';
import { listUsers, useSecurityUsersService } from '../../../services/SecurityUsersService';
import { listInvites } from '../../../services/UserInviteService';
import UserProfileService from '../../../services/UserProfileService';
import TablePaginationBar from '../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../hooks';
import HelpIcon from '../../../components/help/HelpIcon';

// Iconos rotados para las tarjetas de "rol" -- puramente decorativo, no hay
// forma de que el backend indique un icono por rol.
const INVITES_COUNT_CAP = 100;

const GROUP_ICONS = [
  <PeopleIcon sx={{ color: 'var(--wb-accent)' }} />,
  <ShieldOutlinedIcon sx={{ color: 'var(--wb-danger)' }} />,
  <SecurityUpdateGoodIcon sx={{ color: 'var(--wb-success)' }} />
];

const UNASSIGNED_ROLE_ID = '__unassigned__';

const getUserDisplayName = (user) =>
  user.email || user.company || user.name || user.user_id || 'Usuario sin identificar';

const SecurityUsers = () => {
  const navigate = useNavigate();
  const { isPlatformRoot, isTenantAdmin, userData, hasPermission } = useContext(DataContext);
  const canManageUsersSection = Boolean(isPlatformRoot || isTenantAdmin);
  const canReassignRoles = canManageUsersSection || hasPermission('security.users.manage');

  // Mismo proxy de "acceso ya verificado" que UserInvitesPage/SecurityRoles.
  const accessChecked = Boolean(userData);

  const { updateUserRoles, updateUserGroups, updateAccountSecurity } = useSecurityUsersService();

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState('');

  const [groups, setGroups] = useState([]);
  const [groupsError, setGroupsError] = useState('');

  const [pendingInvitesCount, setPendingInvitesCount] = useState(null);
  const [pendingInvitesCapped, setPendingInvitesCapped] = useState(false);
  // {covered, total} -- ver mfa_coverage en la respuesta de
  // GET /useraccount/users (security-roles/app.py::list_account_users).
  const [mfaCoverage, setMfaCoverage] = useState(null);

  const [rowState, setRowState] = useState({});

  // Toggle "Requerir MFA en esta cuenta" -- security.mfa_required se lee del
  // propio perfil del admin (todas las filas del tenant lo comparten, ver
  // update_account_security en user-account/app.py).
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaRequiredLoading, setMfaRequiredLoading] = useState(false);
  const [mfaRequiredError, setMfaRequiredError] = useState('');

  const fetchUsers = useCallback(async () => {
    if (!canManageUsersSection) {
      return;
    }
    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await listUsers();
      // El backend devuelve {items, count, mfa_coverage} -- antes se trataba
      // `response` como si ya fuera el array de usuarios, lo cual dejaba la
      // tabla siempre vacía en silencio.
      setUsers(Array.isArray(response?.items) ? response.items : []);
      setMfaCoverage(response?.mfa_coverage || null);
    } catch (error) {
      setUsersError(error?.message || 'No se pudo obtener el listado de usuarios.');
    } finally {
      setUsersLoading(false);
    }
  }, [canManageUsersSection]);

  const fetchAccountSecurity = useCallback(async () => {
    if (!canManageUsersSection) {
      return;
    }
    try {
      const profile = await UserProfileService.getProfile();
      setMfaRequired(Boolean(profile?.security?.mfa_required));
    } catch (error) {
      console.warn('⚠️ No se pudo obtener la política de seguridad de la cuenta:', error);
    }
  }, [canManageUsersSection]);

  const fetchRoles = useCallback(async () => {
    if (!canManageUsersSection) {
      return;
    }
    setRolesError('');
    try {
      const response = await listRoles();
      // Mismo bug de forma de respuesta ya corregido para listUsers()
      // arriba -- listRoles() también devuelve {items, count}, nunca un
      // array directo.
      setRoles(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setRolesError(error?.message || 'No se pudo obtener el catálogo de roles.');
    }
  }, [canManageUsersSection]);

  const fetchGroups = useCallback(async () => {
    if (!canManageUsersSection) {
      return;
    }
    setGroupsError('');
    try {
      const response = await listGroups();
      setGroups(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setGroupsError(error?.message || 'No se pudo obtener el catálogo de grupos.');
    }
  }, [canManageUsersSection]);

  const fetchPendingInvites = useCallback(async () => {
    if (!canManageUsersSection) {
      return;
    }
    try {
      // Se pide una tanda grande solo para el contador: el backend pagina
      // con cursor y no puede dar un total sin recorrer todo. Si queda
      // cursor, hay más de INVITES_COUNT_CAP y la tarjeta muestra "100+".
      const { items, nextCursor } = await listInvites({
        status: 'PENDING', limit: INVITES_COUNT_CAP
      });
      setPendingInvitesCount(items.length);
      setPendingInvitesCapped(Boolean(nextCursor));
    } catch (error) {
      console.warn('⚠️ No se pudo obtener el conteo de invitaciones pendientes:', error);
      setPendingInvitesCount(null);
    }
  }, [canManageUsersSection]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchGroups();
    fetchPendingInvites();
    fetchAccountSecurity();
  }, [fetchUsers, fetchRoles, fetchGroups, fetchPendingInvites, fetchAccountSecurity]);

  const handleMfaRequiredToggle = async (event) => {
    const nextValue = event.target.checked;
    setMfaRequiredLoading(true);
    setMfaRequiredError('');
    try {
      await updateAccountSecurity({ mfaRequired: nextValue });
      setMfaRequired(nextValue);
    } catch (error) {
      setMfaRequiredError(error?.message || 'No se pudo actualizar la política de MFA.');
    } finally {
      setMfaRequiredLoading(false);
    }
  };

  // Un usuario puede tener varios roles (role_ids) -- cada rol del usuario
  // cuenta hacia la tarjeta correspondiente, no solo "el" rol.
  const roleGroups = useMemo(() => {
    const roleById = new Map(roles.map((role) => [role.role_id, role]));
    const counts = new Map();

    users.forEach((user) => {
      const userRoleIds = Array.isArray(user.role_ids) && user.role_ids.length > 0
        ? user.role_ids
        : [UNASSIGNED_ROLE_ID];
      userRoleIds.forEach((roleId) => {
        counts.set(roleId, (counts.get(roleId) || 0) + 1);
      });
    });

    const groups = roles.map((role) => ({
      key: role.role_id,
      name: role.name,
      scope: role.description || 'Sin descripción',
      members: counts.get(role.role_id) || 0
    }));

    const unassignedCount = counts.get(UNASSIGNED_ROLE_ID) || 0;
    if (unassignedCount > 0) {
      groups.push({
        key: UNASSIGNED_ROLE_ID,
        name: 'Sin rol asignado',
        scope: 'Usuarios pendientes de asignación de rol',
        members: unassignedCount
      });
    }

    return { groups, roleById };
  }, [roles, users]);

  const groupById = useMemo(() => new Map(groups.map((group) => [group.group_id, group])), [groups]);

  const { pageRows, paginationProps } = useTablePagination(users, {
    storageKey: 'securityUsers'
  });

  const handleRolesChange = async (user, newRoleIds) => {
    const userId = user.user_id;
    setRowState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), loading: true, feedback: null } }));
    try {
      await updateUserRoles(userId, newRoleIds);
      const roleNames = newRoleIds
        .map((roleId) => roleGroups.roleById.get(roleId)?.name)
        .filter(Boolean);
      setUsers((prev) =>
        prev.map((item) =>
          item.user_id === userId
            ? { ...item, role_ids: newRoleIds, role_names: roleNames }
            : item
        )
      );
      setRowState((prev) => ({
        ...prev,
        [userId]: { loading: false, feedback: { severity: 'success', message: 'Roles actualizados.' } }
      }));
    } catch (error) {
      setRowState((prev) => ({
        ...prev,
        [userId]: {
          loading: false,
          feedback: { severity: 'error', message: error?.message || 'No se pudo actualizar los roles.' }
        }
      }));
    }
  };

  const handleGroupsChange = async (user, newGroupIds) => {
    const userId = user.user_id;
    setRowState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), loading: true, feedback: null } }));
    try {
      await updateUserGroups(userId, newGroupIds);
      const groupNames = newGroupIds
        .map((groupId) => groupById.get(groupId)?.name)
        .filter(Boolean);
      setUsers((prev) =>
        prev.map((item) =>
          item.user_id === userId
            ? { ...item, group_ids: newGroupIds, group_names: groupNames }
            : item
        )
      );
      setRowState((prev) => ({
        ...prev,
        [userId]: { loading: false, feedback: { severity: 'success', message: 'Grupos actualizados.' } }
      }));
    } catch (error) {
      setRowState((prev) => ({
        ...prev,
        [userId]: {
          loading: false,
          feedback: { severity: 'error', message: error?.message || 'No se pudo actualizar los grupos.' }
        }
      }));
    }
  };

  const handleInviteClick = () => {
    navigate('/admin/home/seguridad/invitaciones');
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Usuarios" description="Gestiona perfiles, accesos y roles de la cuenta.">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManageUsersSection) {
    return (
      <PageContainer
        title="Usuarios"
        description="Solo administradores de la cuenta pueden gestionar los usuarios y sus roles."
      >
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas privilegios de administrador (de tu cuenta o de la plataforma) para acceder a esta sección.
            Contacta al administrador de la cuenta si crees que se trata de un error.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Usuarios"
      description="Gestiona perfiles, accesos y flujo de aprobaciones para todas las unidades de negocio."
      actions={[
        <Button
          key="invite"
          variant="contained"
          color="primary"
          onClick={handleInviteClick}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Invitar usuario
        </Button>
      ]}
    >
      <StatGrid
        items={[
          { label: 'Usuarios activos', value: String(users.length), trend: 'Total en la cuenta' },
          {
            label: 'Invitaciones pendientes',
            value: pendingInvitesCount === null
              ? '—'
              : `${pendingInvitesCount}${pendingInvitesCapped ? '+' : ''}`,
            trend: pendingInvitesCount === null ? 'No disponible' : 'En espera de aceptación'
          },
          { label: 'Accesos revocados', value: '—', trend: 'Sin datos disponibles' },
          {
            label: 'Cobertura MFA',
            value: mfaCoverage ? `${mfaCoverage.covered}/${mfaCoverage.total}` : '—',
            trend: mfaCoverage
              ? `${Math.round((mfaCoverage.covered / (mfaCoverage.total || 1)) * 100)}% con TOTP activo`
              : 'Sin datos disponibles'
          }
        ]}
      />

      {(usersError || rolesError || groupsError) && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
          {usersError || rolesError || groupsError}
        </Alert>
      )}

      <Section
        title="Cartera de usuarios"
        subtitle="Segmenta por rol, área y nivel de exposición a datos sensibles"
      >
        {usersLoading && users.length === 0 && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Cargando usuarios...</Typography>
          </Stack>
        )}

        {!usersLoading && roleGroups.groups.length === 0 && !usersError && (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 2 }}>
            No hay usuarios para mostrar todavía.
          </Typography>
        )}

        <Grid container spacing={3}>
          {roleGroups.groups.map((group, index) => (
            <Grid item xs={12} md={4} key={group.key}>
              <Box
                sx={{
                  backgroundColor: 'var(--wb-bg-surface)',
                  borderRadius: 2,
                  border: '1px solid var(--wb-border-10)',
                  p: 3,
                  height: '100%'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  {GROUP_ICONS[index % GROUP_ICONS.length]}
                  <Box>
                    <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{group.name}</Typography>
                    <Typography sx={{ color: 'var(--wb-muted-75)' }}>
                      {group.members} {group.members === 1 ? 'miembro' : 'miembros'}
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{ color: 'var(--wb-muted-76)' }}>{group.scope}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Section
        title="Usuarios de la cuenta"
        subtitle="Reasigna los roles y grupos de cualquier usuario de tu cuenta -- puede pertenecer a varios a la vez"
      >
        <Box sx={{ position: 'relative', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 720,
              '& th': { color: 'var(--wb-x-text-94a3b8e6, rgba(148, 163, 184, 0.9))', fontWeight: 600, borderBottomColor: 'var(--wb-border)' },
              '& td': { color: 'var(--wb-text)', borderBottomColor: 'var(--wb-border-06)' }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Roles</span>
                    <HelpIcon module="seguridad" topic="usuarios" anchor="roles" />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Grupos</span>
                    <HelpIcon module="seguridad" topic="usuarios" anchor="grupos" />
                  </Stack>
                </TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && !usersLoading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography sx={{ color: 'var(--wb-muted-70)', textAlign: 'center', py: 3 }}>
                      No hay usuarios para mostrar.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {pageRows.map((user) => {
                const rowInfo = rowState[user.user_id] || {};
                const userRoleIds = Array.isArray(user.role_ids) ? user.role_ids : [];
                const userGroupIds = Array.isArray(user.group_ids) ? user.group_ids : [];
                return (
                  <TableRow key={user.user_id}>
                    <TableCell>{getUserDisplayName(user)}</TableCell>
                    <TableCell>
                      <FormControl
                        size="small"
                        disabled={!canReassignRoles || rowInfo.loading}
                        sx={{
                          minWidth: 220,
                          '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' }
                        }}
                      >
                        <Select
                          multiple
                          displayEmpty
                          value={userRoleIds}
                          onChange={(event) => handleRolesChange(user, event.target.value)}
                          renderValue={(selected) => (
                            selected.length === 0
                              ? 'Sin rol asignado'
                              : selected.map((roleId) => roleGroups.roleById.get(roleId)?.name || roleId).join(', ')
                          )}
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.role_id} value={role.role_id}>
                              <Checkbox checked={userRoleIds.includes(role.role_id)} />
                              <ListItemText primary={role.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl
                        size="small"
                        disabled={!canReassignRoles || rowInfo.loading}
                        sx={{
                          minWidth: 220,
                          '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' }
                        }}
                      >
                        <Select
                          multiple
                          displayEmpty
                          value={userGroupIds}
                          onChange={(event) => handleGroupsChange(user, event.target.value)}
                          renderValue={(selected) => (
                            selected.length === 0
                              ? 'Sin grupos'
                              : selected.map((groupId) => groupById.get(groupId)?.name || groupId).join(', ')
                          )}
                        >
                          {groups.map((group) => (
                            <MenuItem key={group.group_id} value={group.group_id}>
                              <Checkbox checked={userGroupIds.includes(group.group_id)} />
                              <ListItemText primary={group.name} />
                            </MenuItem>
                          ))}
                          {groups.length === 0 && (
                            <MenuItem value="" disabled>
                              No hay grupos creados
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {rowInfo.loading && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} />
                          <Typography sx={{ color: 'var(--wb-muted-80)' }}>Guardando...</Typography>
                        </Stack>
                      )}
                      {!rowInfo.loading && rowInfo.feedback && (
                        <Typography
                          sx={{
                            color: rowInfo.feedback.severity === 'success' ? 'var(--wb-success)' : 'var(--wb-danger)',
                            fontSize: '0.85rem'
                          }}
                        >
                          {rowInfo.feedback.message}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {users.length > 0 && <TablePaginationBar {...paginationProps} />}

          {usersLoading && users.length > 0 && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'var(--wb-bg-overlay-70)'
              }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ color: 'var(--wb-text-label)' }}>Actualizando usuarios...</Typography>
            </Stack>
          )}
        </Box>
      </Section>

      <Section
        title="Política de MFA de la cuenta"
        subtitle="Exige verificación en dos pasos a todos los usuarios de tu cuenta"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Switch
            checked={mfaRequired}
            disabled={!canReassignRoles || mfaRequiredLoading}
            onChange={handleMfaRequiredToggle}
          />
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>
                Requerir MFA en esta cuenta
              </Typography>
              <HelpIcon module="seguridad" topic="usuarios" anchor="requerir-mfa-en-esta-cuenta" />
            </Stack>
            <Typography sx={{ color: 'var(--wb-muted-75)' }}>
              Los usuarios sin verificación en dos pasos configurada deberán activarla en su próximo inicio de sesión.
            </Typography>
          </Box>
          {mfaRequiredLoading && <CircularProgress size={20} />}
        </Stack>
        {mfaRequiredError && (
          <Alert severity="error" sx={{ mt: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
            {mfaRequiredError}
          </Alert>
        )}
      </Section>

      <Section
        title="Alertas de seguridad"
        subtitle="Responde a eventos críticos y notificaciones programadas"
      >
        <List>
          {[
            {
              title: 'Revisión trimestral de accesos',
              detail: 'Próxima ejecución: 15 octubre · responsables: Data Governance',
              icon: <NotificationsActiveIcon sx={{ color: 'var(--wb-warning)' }} />
            },
            {
              title: 'Caducidad de credenciales compartidas',
              detail: '3 usuarios deben rotar llaves antes del 30 septiembre',
              icon: <SecurityUpdateGoodIcon sx={{ color: 'var(--wb-danger)' }} />
            }
          ].map((alert) => (
            <ListItem key={alert.title} sx={{ px: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {alert.icon}
                <Box>
                  <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{alert.title}</Typography>
                  <Typography sx={{ color: 'var(--wb-muted-75)', mt: 0.5 }}>{alert.detail}</Typography>
                </Box>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Section>
    </PageContainer>
  );
};

export default SecurityUsers;
